import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Calculator, CreditCard, Plus, RefreshCcw, ShieldAlert, Tags } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DataErrorBanner, type DataErrorDetail } from '@/components/ui/data-error-banner';
import { api, type PaginatedData } from '@/lib/api';
import { useToast } from '@/lib/toast';
import { formatMoney, type DepotBillableCharge, useDepotList } from './depot-hooks';
import {
  DepotEmptyRow,
  DepotGridPanel,
  depotTableClassName,
  depotTableHeadClassName,
  depotTdClassName,
  depotThClassName,
} from './depot-grid';

type DepotRateCard = {
  id: string;
  description?: string | null;
  base_rate: string | number;
  currency: string;
  valid_from?: string | null;
  valid_to?: string | null;
  data?: {
    module?: string;
    seed?: string;
    billToName?: string;
    chargeType?: string;
    unit?: string;
    freeDays?: number;
    notes?: string;
  } | null;
};

type TariffForm = {
  billToName: string;
  chargeType: string;
  unit: string;
  rate: string;
  freeDays: string;
  validFrom: string;
  validTo: string;
  notes: string;
};

const EMPTY_FORM: TariffForm = {
  billToName: '',
  chargeType: '',
  unit: 'per_container',
  rate: '',
  freeDays: '',
  validFrom: '',
  validTo: '',
  notes: '',
};

function normalizeKey(value?: string | null): string {
  return value?.trim() || 'Unassigned';
}

function toAmount(value: string | number | null | undefined): number {
  const parsed = typeof value === 'string' ? Number(value) : value ?? 0;
  return Number.isFinite(parsed) ? Number(parsed) : 0;
}

export default function DepotTariffPage() {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const billable = useDepotList<DepotBillableCharge>('/depot/charges/billable?limit=100');
  const handoffs = useDepotList<DepotBillableCharge>('/depot/charges/handoffs?limit=100');
  const [refreshing, setRefreshing] = useState(false);
  const [rateCards, setRateCards] = useState<DepotRateCard[]>([]);
  const [rateCardError, setRateCardError] = useState<string | null>(null);
  const [rateCardLoading, setRateCardLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<TariffForm>(EMPTY_FORM);
  const accountFilter = searchParams.get('account')?.trim() ?? '';
  const filterKey = normalizeKey(accountFilter);

  const error = billable.error ?? handoffs.error ?? rateCardError;
  const loading = billable.loading || handoffs.loading || rateCardLoading;

  const pageError: DataErrorDetail | null = error
    ? {
        endpoint: '/depot/charges/billable + /depot/charges/handoffs + /rate-cards',
        message: error,
        status: 500,
        code: 'DEPOT_TARIFF_LOAD_FAILED',
      }
    : null;

  const loadRateCards = async () => {
    setRateCardLoading(true);
    setRateCardError(null);
    try {
      const response = await api.get<PaginatedData<DepotRateCard>>('/rate-cards?limit=100');
      setRateCards(response.items.filter((item) => item.data?.module === 'depot'));
    } catch (err) {
      setRateCardError(err instanceof Error ? err.message : 'Failed to load depot tariff templates');
    } finally {
      setRateCardLoading(false);
    }
  };

  useEffect(() => {
    void loadRateCards();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([billable.refresh(), handoffs.refresh(), loadRateCards()]);
    setRefreshing(false);
  };

  const visibleBillable = useMemo(
    () => (accountFilter ? billable.items.filter((item) => normalizeKey(item.bill_to_name ?? item.line_operator) === filterKey) : billable.items),
    [accountFilter, billable.items, filterKey],
  );

  const visibleHandoffs = useMemo(
    () => (accountFilter ? handoffs.items.filter((item) => normalizeKey(item.bill_to_name ?? item.line_operator) === filterKey) : handoffs.items),
    [accountFilter, filterKey, handoffs.items],
  );

  const handleCreate = async () => {
    if (!form.billToName.trim() || !form.chargeType.trim() || !form.rate.trim()) return;
    setSaving(true);
    setRateCardError(null);
    try {
      await api.post('/rate-cards', {
        description: `${form.billToName.trim()} · ${form.chargeType.trim()}`,
        baseRate: Number(form.rate),
        currency: 'SGD',
        validFrom: form.validFrom || undefined,
        validTo: form.validTo || undefined,
        data: {
          module: 'depot',
          billToName: form.billToName.trim(),
          chargeType: form.chargeType.trim(),
          unit: form.unit.trim() || 'per_container',
          freeDays: form.freeDays ? Number(form.freeDays) : undefined,
          notes: form.notes.trim() || undefined,
        },
      });
      toast.success('Depot tariff template created');
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      await loadRateCards();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create depot tariff';
      setRateCardError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const liveTariffs = useMemo(() => {
    const map = new Map<string, {
      chargeType: string;
      count: number;
      total: number;
      avg: number;
      billTos: string[];
      disputedCount: number;
    }>();

    [...visibleBillable, ...visibleHandoffs].forEach((item) => {
      const key = item.charge_type || 'other';
      const current = map.get(key) ?? {
        chargeType: key,
        count: 0,
        total: 0,
        avg: 0,
        billTos: [],
        disputedCount: 0,
      };
      current.count += 1;
      current.total += toAmount(item.amount);
      current.avg = current.total / current.count;
      const billTo = normalizeKey(item.bill_to_name ?? item.line_operator);
      if (!current.billTos.includes(billTo)) current.billTos.push(billTo);
      if (item.charge_status === 'disputed') current.disputedCount += 1;
      map.set(key, current);
    });

    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [visibleBillable, visibleHandoffs]);

  const tariffTemplates = useMemo(() => {
    const rows = accountFilter
      ? rateCards.filter((item) => normalizeKey(item.data?.billToName) === normalizeKey(accountFilter))
      : rateCards;
    return [...rows].sort((a, b) => {
      const aTo = a.valid_to ?? '9999-12-31';
      const bTo = b.valid_to ?? '9999-12-31';
      return aTo.localeCompare(bTo);
    });
  }, [accountFilter, rateCards]);

  const tariffCoverage = useMemo(() => {
    const templateKeys = new Set(
      rateCards.map((item) => `${normalizeKey(item.data?.billToName)}::${normalizeKey(item.data?.chargeType)}`),
    );
    const covered = visibleBillable.filter((item) =>
      templateKeys.has(`${normalizeKey(item.bill_to_name ?? item.line_operator)}::${normalizeKey(item.charge_type)}`),
    ).length;
    const unresolved = visibleBillable.length - covered;
    const disputed = visibleBillable.filter((item) => item.charge_status === 'disputed').length;
    return { covered, unresolved, disputed };
  }, [rateCards, visibleBillable]);

  const templateCoverageByType = useMemo(() => {
    const templateKeys = new Set(
      rateCards.map((item) => `${normalizeKey(item.data?.billToName)}::${normalizeKey(item.data?.chargeType)}`),
    );

    return liveTariffs.map((item) => {
      const coveredAccounts = item.billTos.filter((billTo) =>
        templateKeys.has(`${billTo}::${normalizeKey(item.chargeType)}`),
      ).length;

      return {
        ...item,
        coveredAccounts,
        uncoveredAccounts: item.billTos.length - coveredAccounts,
      };
    });
  }, [liveTariffs, rateCards]);

  return (
    <div className="space-y-5">
      {pageError && <DataErrorBanner error={pageError} entityLabel="Depot Tariff" onRetry={handleRefresh} />}

      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Depot Tariff</h2>
          <p className="text-sm text-muted-foreground">
            Pricing desk for live charge signals, bill-to coverage, and rate-card-backed depot tariff templates.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            New Tariff
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/depot/revenue">Revenue</Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/depot/billing">Billing Desk</Link>
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing || loading}>
            <RefreshCcw className={`mr-1.5 h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {accountFilter && (
            <Button variant="ghost" size="sm" onClick={() => setSearchParams({}, { replace: true })}>
              Clear Filter
            </Button>
          )}
        </div>
      </div>

      {accountFilter && (
        <div className="text-xs text-muted-foreground">
          Filtered account: <span className="font-medium text-foreground">{accountFilter}</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        {[
          { label: 'Charge Types', value: liveTariffs.length, icon: <Tags className="h-4 w-4 text-sky-700" /> },
          { label: 'Tariff Covered', value: tariffCoverage.covered, icon: <CreditCard className="h-4 w-4 text-emerald-700" /> },
          { label: 'Coverage Gap', value: tariffCoverage.unresolved, icon: <Calculator className="h-4 w-4 text-amber-700" /> },
          { label: 'Disputed Lines', value: tariffCoverage.disputed, icon: <ShieldAlert className="h-4 w-4 text-rose-700" /> },
          { label: 'Tariff Templates', value: tariffTemplates.length, icon: <Tags className="h-4 w-4 text-violet-700" /> },
        ].map((item) => (
          <div key={item.label} className="rounded-lg border bg-background px-3 py-3">
            <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
              <span>{item.label}</span>
              {item.icon}
            </div>
            <div className="text-2xl font-bold">{item.value}</div>
          </div>
        ))}
      </div>

      <DepotGridPanel title="Tariff Templates" subtitle="Rate-card-backed pricing templates by bill-to and charge type.">
        <table className={depotTableClassName}>
          <thead className={depotTableHeadClassName}>
            <tr>
              <th className={depotThClassName}>Bill To</th>
              <th className={depotThClassName}>Charge Type</th>
              <th className={depotThClassName}>Unit</th>
              <th className={depotThClassName}>Rate</th>
              <th className={depotThClassName}>Free Days</th>
              <th className={depotThClassName}>Valid From</th>
              <th className={depotThClassName}>Valid To</th>
              <th className={depotThClassName}>Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {tariffTemplates.length === 0 ? (
              <DepotEmptyRow colSpan={8}>No depot tariff templates yet.</DepotEmptyRow>
            ) : (
              tariffTemplates.map((item) => (
                <tr key={item.id} className="hover:bg-muted/30">
                  <td className={`${depotTdClassName} font-medium`}>{item.data?.billToName ?? item.description ?? 'Depot tariff'}</td>
                  <td className={depotTdClassName}>{item.data?.chargeType ?? 'Charge'}</td>
                  <td className={depotTdClassName}>{item.data?.unit ?? 'per_container'}</td>
                  <td className={depotTdClassName}><Badge variant="secondary">{formatMoney(toAmount(item.base_rate), item.currency)}</Badge></td>
                  <td className={depotTdClassName}>{item.data?.freeDays ?? '—'}</td>
                  <td className={depotTdClassName}>{item.valid_from ?? 'Open'}</td>
                  <td className={depotTdClassName}>{item.valid_to ?? 'Open'}</td>
                  <td className={depotTdClassName}>{item.data?.notes ?? '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DepotGridPanel>

      <DepotGridPanel title="Live Tariff Reference" subtitle="Observed charge signals and coverage gap by charge type.">
        <table className={depotTableClassName}>
          <thead className={depotTableHeadClassName}>
            <tr>
              <th className={depotThClassName}>Charge Type</th>
              <th className={depotThClassName}>Total</th>
              <th className={depotThClassName}>Avg Live Rate</th>
              <th className={depotThClassName}>Observed</th>
              <th className={depotThClassName}>Covered Accounts</th>
              <th className={depotThClassName}>Coverage Gap</th>
              <th className={depotThClassName}>Accounts Seen</th>
              <th className={depotThClassName}>Status</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {templateCoverageByType.length === 0 ? (
              <DepotEmptyRow colSpan={8}>No tariff signals yet.</DepotEmptyRow>
            ) : (
              templateCoverageByType.map((item) => (
                <tr key={item.chargeType} className="hover:bg-muted/30">
                  <td className={depotTdClassName}>{item.chargeType}</td>
                  <td className={depotTdClassName}>{formatMoney(item.total)}</td>
                  <td className={depotTdClassName}>{formatMoney(item.avg)}</td>
                  <td className={depotTdClassName}>{item.count}</td>
                  <td className={depotTdClassName}>{item.coveredAccounts}</td>
                  <td className={depotTdClassName}>{item.uncoveredAccounts}</td>
                  <td className={`${depotTdClassName} text-xs text-muted-foreground`}>{item.billTos.join(', ') || 'Unassigned'}</td>
                  <td className={depotTdClassName}>
                    <Badge variant={item.disputedCount > 0 ? 'destructive' : 'secondary'}>
                      {item.disputedCount > 0 ? `${item.disputedCount} disputed` : `${item.count} line(s)`}
                    </Badge>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DepotGridPanel>

      <div className="rounded-lg border bg-muted/20 px-3 py-3 text-sm text-muted-foreground">
        The next production step is a tighter contract model linking `bill-to`, `charge type`, `free days`, and `rate source`.
        <Button variant="ghost" size="sm" asChild className="ml-2 h-auto px-0">
          <Link to="/depot/accounts">See account view</Link>
        </Button>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Depot Tariff</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid gap-2">
              <Label htmlFor="billToName">Bill To</Label>
              <Input id="billToName" value={form.billToName} onChange={(event) => setForm((current) => ({ ...current, billToName: event.target.value }))} placeholder="OOCL / MAERSK / ONE" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="chargeType">Charge Type</Label>
              <Input id="chargeType" value={form.chargeType} onChange={(event) => setForm((current) => ({ ...current, chargeType: event.target.value }))} placeholder="storage / wash / repair / handling" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label htmlFor="rate">Rate</Label>
                <Input id="rate" value={form.rate} onChange={(event) => setForm((current) => ({ ...current, rate: event.target.value }))} placeholder="85" />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="unit">Unit</Label>
                <Input id="unit" value={form.unit} onChange={(event) => setForm((current) => ({ ...current, unit: event.target.value }))} placeholder="per_container" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="grid gap-2">
                <Label htmlFor="freeDays">Free Days</Label>
                <Input id="freeDays" value={form.freeDays} onChange={(event) => setForm((current) => ({ ...current, freeDays: event.target.value }))} placeholder="3" />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="validFrom">Valid From</Label>
                <Input id="validFrom" type="date" value={form.validFrom} onChange={(event) => setForm((current) => ({ ...current, validFrom: event.target.value }))} />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="validTo">Valid To</Label>
                <Input id="validTo" type="date" value={form.validTo} onChange={(event) => setForm((current) => ({ ...current, validTo: event.target.value }))} />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="notes">Notes</Label>
              <Input id="notes" value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} placeholder="Applies to import empties only" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={() => void handleCreate()} disabled={saving}>
              {saving ? 'Saving...' : 'Create Tariff'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
